/// <amd-module name="Artifacts/Nuget/components/NugetPackageAttributes" />
import { NuGetCiConstants } from "Artifacts/Nuget/NuGetConstants";
import { INuGetProtocolMetadata } from "Artifacts/Nuget/NuGetProtocolMetadata";
import * as NugetRes from "Artifacts/Nuget/Resources";
import { NugetDependencyHelper } from "Artifacts/Nuget/helpers/NugetDependencyHelper";
import { INuGetRestApiUrlGeneratorService } from "Artifacts/Nuget/services/NuGetRestApiUrlGeneratorService";
import * as PackageRes from "Artifacts/Package/Resources";
import { PackageAttribute } from "Artifacts/Package/components/package-details-common/PackageAttribute";
import { PackageDependencies } from "Artifacts/Package/components/package-details-common/PackageDependencies";
import {
  IProjectAttribute,
  PackageDevelopment,
} from "Artifacts/Package/components/package-details-common/PackageDevelopment";
import { PackagePeople } from "Artifacts/Package/components/package-details-common/PackagePeople";
import { PackageProvenance } from "Artifacts/Package/components/package-details-common/PackageProvenance";
import { PackageStats } from "Artifacts/Package/components/package-details-common/PackageStats";
import { PackageTags } from "Artifacts/Package/components/package-details-common/PackageTags";
import { IProtocolOverviewProps } from "Artifacts/Root/protocols/types";
import { IFeed, IPackage, IPackageVersion } from "Artifacts/Root/types";
import * as StringUtils from "VSS/Core/Util/String";
import { IVssFeatureService, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useComponentContext } from "VSS/Platform/Layout";
import * as React from "react";

const NugetAttributes: React.FC<IProtocolOverviewProps> = ({ feed, pkg, version }) => {
  const { pageContext } = useComponentContext();
  const isProvenanceEnabled = isFeatureFlagEnabled(
    pageContext,
    "Packaging.Feed.WebAccess.Provenance",
    false,
  );
  const isMetricsEnabled = isFeatureFlagEnabled(
    pageContext,
    "Packaging.Feed.WebAccess.Metrics",
    false,
  );

  const projectAttributes: IProjectAttribute[] = [];
  const nugetMetadata =
    version.protocolMetadata && version.protocolMetadata.data
      ? (version.protocolMetadata.data as INuGetProtocolMetadata)
      : null;

  if (nugetMetadata) {
    if (nugetMetadata.projectUrl) {
      projectAttributes.push({
        iconClass: "Globe",
        text: "Homepage",
        url: nugetMetadata.projectUrl,
        onClickCiFeature: NuGetCiConstants.ProjectLinkClicked,
      } as IProjectAttribute);
    }

    const licenseInfo = _getLicenseInfo(feed, pkg, version, nugetMetadata);
    if (licenseInfo) {
      projectAttributes.push({
        iconClass: "Script",
        text: licenseInfo.title,
        url: licenseInfo.url,
        onClickCiFeature: NuGetCiConstants.LicenseLinkClicked,
      } as IProjectAttribute);
    }
  }

  return (
    <section className="package-overview-attributes" aria-label={PackageRes.PackageMetadata}>
      <div className="nuget-overview-attributes-panel">
        <PackageProvenance
          pageContext={pageContext}
          feed={feed}
          packageId={pkg.id}
          versionId={version.id}
          publishDate={new Date(version.publishDate)}
          isProvenanceEnabled={isProvenanceEnabled}
          isProvenanceSupported={true}
        />
        {version.author != null && <PackagePeople authors={version.author.split(",")} />}
        {
          <PackageDevelopment
            pageContext={pageContext}
            projectAttributes={projectAttributes}
            packageSummary={pkg}
            packageVersion={version}
            upstreamSources={feed.upstreamSources}
          />
        }
        {isMetricsEnabled && (
          <PackageStats
            pageContext={pageContext}
            feed={feed}
            package={pkg}
            versionId={version.id}
            isMetricsEnabled={isMetricsEnabled}
          />
        )}
        {<PackageTags tags={version.tags} />}
        {<PackageTags title={PackageRes.PackageDetails_VersionsTable_Views} tags={version.views} />}
        {
          <PackageDependencies
            pageContext={pageContext}
            feed={feed}
            packageSummary={pkg}
            packageVersion={version}
            dependencyHelperCallback={NugetDependencyHelper.formatVersionRanges}
          />
        }
        {nugetMetadata != null && (
          <div>
            <PackageAttribute title={NugetRes.NuGetMinimumClientVersion_Title}>
              {nugetMetadata.minClientVersion}
            </PackageAttribute>
            <PackageAttribute title={NugetRes.NuGetCopyright_Title}>
              {nugetMetadata.copyright}
            </PackageAttribute>
            <PackageAttribute title={NugetRes.NuGetLanguage_Title}>
              {nugetMetadata.language}
            </PackageAttribute>
          </div>
        )}
      </div>
    </section>
  );
};

function _getLicenseInfo(
  feed: IFeed,
  pkg: IPackage,
  version: IPackageVersion,
  nuGetMetadata: INuGetProtocolMetadata,
): { title: string; url: string | null } | null {
  const { pageContext } = useComponentContext();
  if (!nuGetMetadata) {
    return null;
  }

  const featureService = pageContext.getService<IVssFeatureService>("IVssFeatureService");
  if (
    nuGetMetadata.licenseFile &&
    featureService.isFeatureFlagEnabled("Packaging.Feed.NuGet.EmbeddedLicenseLink", false)
  ) {
    // when removing the above Packaging.Feed.NuGet.EmbeddedFilesUI check, be sure to also remove
    // the fallback below

    const generator = pageContext.getService<INuGetRestApiUrlGeneratorService>(
      "INuGetRestApiUrlGeneratorService",
    );
    return {
      title: NugetRes.NuGetLinks_License,
      url: generator.getExtractFromNupkgUrl(
        feed.id,
        pkg.name,
        version.normalizedVersion,
        nuGetMetadata.licenseFile,
        !!feed.project /* includeProject */,
        feed.accountMetadata.publicAccessMapping,
      ),
    };
  }

  if (nuGetMetadata.licenseExpression) {
    return {
      title: StringUtils.format(
        NugetRes.NuGetLinks_LicenseExpression,
        nuGetMetadata.licenseExpression,
      ),
      url: `https://licenses.nuget.org/${encodeURIComponent(nuGetMetadata.licenseExpression)}`,
    };
  }

  if (nuGetMetadata.licenseUrl) {
    return {
      title: NugetRes.NuGetLinks_License,
      url: nuGetMetadata.licenseUrl,
    };
  }

  if (nuGetMetadata.licenseFile) {
    // fallback (remove this when removing the FF check)
    // If there's an embedded license and neither an expression nor a url, but the FF is off, at least alert
    // the user to the fact that there is a license file embedded
    return {
      title: StringUtils.format(NugetRes.NuGetLinks_LicenseEmbedded, nuGetMetadata.licenseFile),
      url: null,
    };
  }

  return null;
}

export { NugetAttributes };
